import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";
import { LoadingModal } from "@/components/modals/loading-modal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

import { useCreateCheckoutSessionQuery } from "@/redux/services/order-portal";

declare global {
  interface Window {
    Checkout: {
      configure: (config: MPGSConfig) => void;
      showPaymentPage: () => void;
    };
    errorCallback: () => void;
  }
}

export default function Payment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const MPGS_CHECKOUT_URL = import.meta.env.VITE_MPGS_CHECKOUT_URL;

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isProcessingPaymentError, setIsProcessingPaymentError] =
    useState(false);

  const {
    currentData: createCheckoutSessionResponse,
    isLoading: isCreatingCheckoutSession,
    error: createCheckoutSessionError,
    isError: isCreateCheckoutSessionError,
  } = useCreateCheckoutSessionQuery({
    id: id ?? "",
  });

  const configurePaymentPage = (sessionId: string) => {
    window.Checkout.configure({
      session: {
        id: sessionId,
      },
    });

    window.Checkout.showPaymentPage();
  };

  useEffect(() => {
    if (!(MPGS_CHECKOUT_URL && createCheckoutSessionResponse)) {
      return;
    }

    const sessionId = createCheckoutSessionResponse["session.id"];

    setIsProcessingPayment(true);

    // Check if script is already loaded
    if (document.querySelector(`script[src="${MPGS_CHECKOUT_URL}"]`)) {
      // Script already exists, just configure and show payment page
      if (window.Checkout) {
        configurePaymentPage(sessionId);
      }
      return;
    }

    // Create and load the script dynamically
    const script = document.createElement("script");
    script.src = MPGS_CHECKOUT_URL;
    script.setAttribute("data-error", "errorCallback");
    script.async = true;

    script.onload = () => {
      try {
        window.Checkout.configure({
          session: {
            id: createCheckoutSessionResponse["session.id"]
          },
        });

        window.Checkout.showPaymentPage();
      } catch (error) {
        console.error("Error configuring checkout:", error);
        setIsProcessingPayment(false);
        setIsProcessingPaymentError(true);
      }
    };

    script.onerror = () => {
      setIsProcessingPayment(false);
      setIsProcessingPaymentError(true);
    };

    // Append script to head
    document.head.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      const existingScript = document.querySelector(
        `script[src="${MPGS_CHECKOUT_URL}"]`,
      );
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [createCheckoutSessionResponse]);

  useEffect(() => {
    window.errorCallback = () => {
      setIsProcessingPayment(false);
      setIsProcessingPaymentError(true);
    };

    // Cleanup function
    return () => {
      if ("errorCallback" in window) {
        // biome-ignore lint/performance/noDelete: Necessary
        // biome-ignore lint/suspicious/noExplicitAny: Necessary
        delete (window as any).errorCallback;
      }
    };
  }, []);

  if (!id) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <LoadingModal isOpen={isCreatingCheckoutSession || isProcessingPayment} />

      <AlertDialog
        open={isCreateCheckoutSessionError || isProcessingPaymentError}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {(createCheckoutSessionError as APIErrorResponse)?.data.error ===
              "ALREADY_PAID"
                ? t("errors.alreadyPaid")
                : t("errors.generic")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(createCheckoutSessionError as APIErrorResponse)?.data.error ===
              "ALREADY_PAID"
                ? t("errors.alreadyPaid")
                : t("errors.generic")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction asChild>
              <Button asChild>
                <Button onClick={() => navigate("/")}>
                  {t("labels.return-to-homepage")}
                </Button>
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
